import { useCallback, useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { API_URL, MODULOS } from '../api/config';

/**
 * Monta os links que o treinador abre nos tablets.
 *
 * Antes, os modulos eram cinco URLs escritas a mao em Modules.tsx, quatro delas em http://,
 * e nenhuma levava a sala. O operador abria a URL, fazia login e escolhia a sala numa lista —
 * tres chances de errar antes de ler o primeiro cracha.
 *
 * Aqui a lista de salas vem da API, entao o link ja nasce apontando para a sala certa e some
 * a escolha manual. Nao ha entidade de terminal ainda: isto e geracao de link a partir do dado
 * que ja existe. Quando os terminais existirem, esta tela troca a fonte e mantem o formato.
 */

export interface Sala {
  uid: string;
  roomName: string;
  totalSlots: number;
  location?: string;
}

export interface LinkDeTerminal {
  chave: string;
  titulo: string;
  subtitulo: string;
  url: string;
  qrDataUrl: string | null;
  tipo: 'porta' | 'balcao' | 'totem';
}

/** Os tres modulos de campo. Balcao e totem nao tem sala; a porta tem uma por sala. */
const MODULOS_FIXOS: Array<{ chave: string; titulo: string; subtitulo: string; url: string; tipo: 'balcao' | 'totem' }> = [
  {
    chave: 'balcao',
    titulo: 'Balcao de credenciamento',
    subtitulo: 'Operador credencia e imprime o cracha',
    url: MODULOS.credenciamento,
    tipo: 'balcao',
  },
  {
    chave: 'totem',
    titulo: 'Totem de autoatendimento',
    subtitulo: 'O participante se atende sozinho',
    url: MODULOS.autoatendimento,
    tipo: 'totem',
  },
];

export const useTerminalLinks = () => {
  const [salas, setSalas] = useState<Sala[]>([]);
  const [qrPorChave, setQrPorChave] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buscarSalas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resposta = await fetch(`${API_URL}room`);
      if (!resposta.ok) throw new Error(`Servidor respondeu ${resposta.status}`);
      const dados = await resposta.json();
      if (!Array.isArray(dados)) throw new Error('Resposta inesperada do servidor');
      setSalas(
        dados
          .filter((s: any) => s?.uid && s?.roomName)
          .map((s: any) => ({
            uid: s.uid,
            roomName: s.roomName,
            totalSlots: s.totalSlots ?? 0,
            location: s.location || undefined,
          })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nao foi possivel carregar as salas');
      setSalas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    buscarSalas();
  }, [buscarSalas]);

  /** Links sem o QR — o QR e assincrono e entra depois, por chave. */
  const linksBase = useMemo(() => {
    const portas = salas.map((sala) => ({
      chave: `porta-${sala.uid}`,
      titulo: sala.roomName,
      subtitulo:
        sala.totalSlots > 0
          ? `Porta de sala · ${sala.totalSlots} lugares`
          : 'Porta de sala',
      // fixa=1 trava o tablet nesta sala: sair pede confirmacao. O link so tem valor se
      // carregar uma decisao — sem isso qualquer link servia para qualquer sala, e abrir
      // na sala errada agora grava presenca na palestra de outra sala.
      url: `${MODULOS.acesso}/room?uid=${encodeURIComponent(sala.uid)}&fixa=1`,
      tipo: 'porta' as const,
    }));
    return [...portas, ...MODULOS_FIXOS];
  }, [salas]);

  // O QR e gerado no navegador, sem servico externo: um QR de link de terminal nao deve
  // trafegar por terceiro, e a tela precisa funcionar na rede do evento.
  useEffect(() => {
    let cancelado = false;
    const gerar = async () => {
      const entradas = await Promise.all(
        linksBase.map(async (l) => {
          try {
            const dataUrl = await QRCode.toDataURL(l.url, {
              margin: 1,
              width: 320,
              errorCorrectionLevel: 'M',
            });
            return [l.chave, dataUrl] as const;
          } catch {
            return [l.chave, ''] as const;
          }
        }),
      );
      if (!cancelado) {
        setQrPorChave(Object.fromEntries(entradas.filter(([, v]) => v)));
      }
    };
    if (linksBase.length) gerar();
    return () => {
      cancelado = true;
    };
  }, [linksBase]);

  const links: LinkDeTerminal[] = useMemo(
    () => linksBase.map((l) => ({ ...l, qrDataUrl: qrPorChave[l.chave] ?? null })),
    [linksBase, qrPorChave],
  );

  return {
    links,
    portas: links.filter((l) => l.tipo === 'porta'),
    fixos: links.filter((l) => l.tipo !== 'porta'),
    salas,
    loading,
    error,
    recarregar: buscarSalas,
  };
};
